import { useCallback, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';

/** The Tiptap node name registered by the CustomHeading extension. */
const HEADING_NODE_NAME = 'heading';

/** State and actions for editing a heading's subsection ID and copying its anchor. */
export interface SubsectionIdEditing {
  /** Whether the subsection-ID editing modal is open. */
  readonly isSubsectionIdModalVisible: boolean;
  /** Value to seed the modal with, read from the selected heading's `id`. */
  readonly initialSubsectionIdValue: string;
  /** Opens the subsection-ID modal for the heading currently selected in the editor. */
  readonly openSubsectionIdModal: (editor: Editor) => void;
  /** Writes the edited subsection ID back onto the selected heading and closes the modal. */
  readonly saveSubsectionIdValue: (value: string) => void;
  /** Closes the subsection-ID modal without saving. */
  readonly dismissSubsectionIdModal: () => void;
  /** Copies the `#fragment` anchor of the selected heading to the clipboard. */
  readonly copySubsectionAnchor: (editor: Editor) => void;
}

/** Locates the heading that contains the current selection, with its document position. */
function findSelectedHeading(editor: Editor) {
  const { from } = editor.state.selection;

  // Walk up from the cursor: the selection sits inside the heading's text.
  const $pos = editor.state.doc.resolve(from);
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === HEADING_NODE_NAME) return { node, pos: $pos.before(depth) };
  }

  // Fallback: the node may start exactly at the cursor position.
  const nodeAt = editor.state.doc.nodeAt(from);
  if (nodeAt?.type.name === HEADING_NODE_NAME) return { node: nodeAt, pos: from };

  return undefined;
}

/**
 * Drives the heading subsection-ID modal for a v2 editor.
 *
 * The write-back runs a Tiptap command, so the edit flows out through the
 * editor's existing `onChange` and is persisted by the surrounding editor's
 * staging path — no manual document patching needed.
 */
export function useSubsectionIdEditing(editor: Editor | undefined): SubsectionIdEditing {
  const [isSubsectionIdModalVisible, setModalVisible] = useState(false);
  const [initialSubsectionIdValue, setInitialValue] = useState('');

  // The document position of the heading being edited; read on save so the
  // command targets the right node even after the editor loses focus.
  const nodePosRef = useRef<number | undefined>(undefined);

  // When the editor's bubble menu requests subsection-ID editing, read the
  // selected heading's current id and open the modal.
  const openSubsectionIdModal = useCallback((ed: Editor) => {
    const heading = findSelectedHeading(ed);
    if (!heading) return;

    nodePosRef.current = heading.pos;
    setInitialValue((heading.node.attrs.id as string) ?? '');
    setModalVisible(true);
  }, []);

  // When the user saves, write the id onto the selected heading; the resulting
  // document change propagates through the editor's onChange for persistence.
  const saveSubsectionIdValue = useCallback(
    (value: string) => {
      const pos = nodePosRef.current;
      if (editor && pos !== undefined) {
        editor
          .chain()
          .setNodeSelection(pos)
          .updateAttributes(HEADING_NODE_NAME, { id: value.trim() || null })
          .run();
      }
      setModalVisible(false);
    },
    [editor],
  );

  const dismissSubsectionIdModal = useCallback(() => setModalVisible(false), []);

  // When the bubble menu requests a copy, write the heading's `#fragment` anchor
  // to the clipboard; do nothing when the heading has no id yet.
  const copySubsectionAnchor = useCallback((ed: Editor) => {
    const id = findSelectedHeading(ed)?.node.attrs.id as string | null | undefined;
    if (id) navigator.clipboard.writeText(`#${id}`);
  }, []);

  return {
    isSubsectionIdModalVisible,
    initialSubsectionIdValue,
    openSubsectionIdModal,
    saveSubsectionIdValue,
    dismissSubsectionIdModal,
    copySubsectionAnchor,
  };
}
